import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronRight,
  ChevronDown,
  Table2,
  Plus,
  Search,
  Loader2,
  UserCircle2,
} from "lucide-react";
import { toast } from "react-hot-toast";
import spreadsheetConnectorService, {
  CredentialGroup,
  ConnectedFile,
  SpreadsheetConnector,
} from "../../services/spreadsheet-connector.service";
import documentStoresService from "../../services/document-stores.service";
import ConnectorStatusBadge from "../../components/documents/spreadsheet-connector/ConnectorStatusBadge";
import ConnectorRowActions, {
  ConnectorActionItem,
} from "../../components/documents/spreadsheet-connector/ConnectorRowActions";
import ConnectSheetModal from "../../components/documents/spreadsheet-connector/ConnectSheetModal";
import SyncHistoryModal from "../../components/documents/spreadsheet-connector/SyncHistoryModal";
import DeleteConfirmModal from "../../components/documents/DeleteConfirmModal";

const PAGE_SIZE = 10;

export default function SpreadsheetConnectorList() {
  const { documentStoreId } = useParams<{ documentStoreId: string }>();
  const navigate = useNavigate();

  const [storeName, setStoreName] = useState("");
  const [credentials, setCredentials] = useState<CredentialGroup[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  // Which credential rows are expanded
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const [showConnectModal, setShowConnectModal] = useState(false);
  const [credentialId, setCredentialId] = useState<string | undefined>(undefined);

  const [deleteTarget, setDeleteTarget] = useState<ConnectorActionItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  const [historyTarget, setHistoryTarget] = useState<ConnectorActionItem | null>(null);

  // ── Store name ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!documentStoreId) return;
    documentStoresService
      .getDocumentStoreById(documentStoreId)
      .then((store) => setStoreName(store.folder_name))
      .catch(() => {});
  }, [documentStoreId]);

  // ── OAuth callback ────────────────────────────────────────────────────────
  useEffect(() => {
    const stored = sessionStorage.getItem("sc_credential_id");
    if (stored) {
      sessionStorage.removeItem("sc_credential_id");
      setCredentialId(stored);
      setShowConnectModal(true);
    }
  }, []);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchConnectors = useCallback(
    async (page: number, search: string) => {
      if (!documentStoreId) return;
      setLoading(true);
      try {
        const result = await spreadsheetConnectorService.listActiveConnectors({
          document_store_id: documentStoreId,
          ...(search ? { search } : {}),
          skip: (page - 1) * PAGE_SIZE,
          limit: PAGE_SIZE,
        });
        setCredentials(result.credentials);
        setTotalCount(result.total_count);
      } catch {
        toast.error("Failed to load spreadsheet connectors");
      } finally {
        setLoading(false);
      }
    },
    [documentStoreId]
  );

  useEffect(() => {
    fetchConnectors(currentPage, searchTerm);
  }, [fetchConnectors, currentPage, searchTerm]);

  // ── Accordion toggle ──────────────────────────────────────────────────────
  const toggleExpand = (credId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(credId) ? next.delete(credId) : next.add(credId);
      return next;
    });
  };

  // ── Actions ───────────────────────────────────────────────────────────────
  const handlePause = async (id: string) => {
    setActionLoadingId(id);
    try {
      await spreadsheetConnectorService.pauseConnector(id);
      toast.success("Connector paused");
      fetchConnectors(currentPage, searchTerm);
    } catch {
      toast.error("Failed to pause connector");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleResume = async (id: string) => {
    setActionLoadingId(id);
    try {
      await spreadsheetConnectorService.resumeConnector(id);
      toast.success("Connector resumed");
      fetchConnectors(currentPage, searchTerm);
    } catch {
      toast.error("Failed to resume connector");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await spreadsheetConnectorService.deleteConnector(deleteTarget._id);
      toast.success("Connector deleted");
      setDeleteTarget(null);
      fetchConnectors(currentPage, searchTerm);
    } catch {
      toast.error("Failed to delete connector");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreated = (created: SpreadsheetConnector[]) => {
    setShowConnectModal(false);
    setCredentialId(undefined);
    fetchConnectors(1, searchTerm);
    setCurrentPage(1);
    toast.success(
      `${created.length} spreadsheet${created.length !== 1 ? "s" : ""} connected successfully`
    );
  };

  const handleOpenConnect = () => {
    setCredentialId(undefined);
    setShowConnectModal(true);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const formatDate = (iso: string | null) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getInitials = (name: string) =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  const toActionItem = (file: ConnectedFile): ConnectorActionItem => ({
    _id: file._id,
    sync_status: file.sync_status,
    spreadsheet_name: file.spreadsheet_name,
  });

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="mx-auto space-y-3">
        {/* Page header */}
        <div className="bg-white border border-gray-200 px-5 py-4 space-y-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Table2 className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <h1 className="text-base font-semibold text-gray-900">Spreadsheet Connector</h1>
                <p className="text-xs text-gray-500">Google Sheets → Document Store sync</p>
              </div>
            </div>
            <button
              onClick={handleOpenConnect}
              className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Connect Google Sheet
            </button>
          </div>

          {/* Breadcrumb */}
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <button
              onClick={() => navigate(`/files/${documentStoreId}`)}
              className="hover:text-gray-700 transition-colors"
            >
              {storeName || "Document Store"}
            </button>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gray-700 font-medium">Spreadsheet Connector</span>
          </div>
        </div>

        {/* Search + accordion list */}
        <div className="bg-white border border-gray-200 rounded-lg">
          {/* Search bar */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="relative max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search spreadsheets…"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                  setExpandedIds(new Set());
                }}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-16 gap-2 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Loading connectors…</span>
            </div>
          ) : credentials.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Table2 className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-700 mb-1">No connectors yet</p>
              <p className="text-xs text-gray-500 mb-4">
                Connect a Google Sheet to start syncing rows into this document store.
              </p>
              <button
                onClick={handleOpenConnect}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Connect Google Sheet
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {credentials.map((cred) => {
                const isExpanded = expandedIds.has(cred.credential_id);
                const fileCount = cred.connected_files.length;

                return (
                  <div key={cred.credential_id}>
                    {/* ── Credential header row ── */}
                    <button
                      onClick={() => toggleExpand(cred.credential_id)}
                      className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors text-left"
                    >
                      {/* Avatar */}
                      <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 text-xs font-semibold text-green-700">
                        {cred.user_name ? getInitials(cred.user_name) : <UserCircle2 className="w-5 h-5" />}
                      </div>

                      {/* Name + email */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {cred.user_name}
                        </p>
                        <p className="text-xs text-gray-400 truncate">{cred.user_email}</p>
                      </div>

                      {/* File count badge */}
                      <span className="flex-shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        {fileCount} {fileCount === 1 ? "spreadsheet" : "spreadsheets"}
                      </span>

                      {/* Chevron */}
                      <ChevronDown
                        className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
                          isExpanded ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {/* ── Expanded files table ── */}
                    {isExpanded && (
                      <div className="border-t border-gray-100 bg-gray-50">
                        {fileCount === 0 ? (
                          <p className="text-xs text-gray-400 px-6 py-4">
                            No spreadsheets connected for this account.
                          </p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-gray-200">
                                  <th className="text-left pl-14 pr-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Spreadsheet
                                  </th>
                                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                  </th>
                                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Tabs
                                  </th>
                                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Total Rows
                                  </th>
                                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Last Synced
                                  </th>
                                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {cred.connected_files.map((file) => (
                                  <tr
                                    key={file._id}
                                    className="hover:bg-white transition-colors"
                                  >
                                    <td className="pl-14 pr-4 py-3">
                                      <p className="font-medium text-gray-800 truncate max-w-xs">
                                        {file.spreadsheet_name}
                                      </p>
                                    </td>
                                    <td className="px-4 py-3">
                                      <ConnectorStatusBadge status={file.sync_status} />
                                    </td>
                                    <td className="px-4 py-3 text-gray-600">
                                      {file.sheets_metadata?.length ?? 0}
                                    </td>
                                    <td className="px-4 py-3 text-gray-600">
                                      {file.total_rows?.toLocaleString() ?? "—"}
                                    </td>
                                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                                      {formatDate(file.last_synced_at)}
                                    </td>
                                    <td className="px-4 py-3">
                                      <ConnectorRowActions
                                        connector={toActionItem(file)}
                                        onPause={handlePause}
                                        onResume={handleResume}
                                        onDelete={setDeleteTarget}
                                        onHistory={setHistoryTarget}
                                        isActionLoading={actionLoadingId === file._id}
                                        isDeleteLoading={isDeleting && deleteTarget?._id === file._id}
                                      />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <span className="text-xs text-gray-500">
                Page {currentPage} of {totalPages} &nbsp;·&nbsp; {totalCount} accounts
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <ConnectSheetModal
        isOpen={showConnectModal}
        onClose={() => {
          setShowConnectModal(false);
          setCredentialId(undefined);
        }}
        documentStoreId={documentStoreId ?? ""}
        credentialId={credentialId}
        onCreated={handleCreated}
      />

      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        itemName={deleteTarget?.spreadsheet_name ?? ""}
        itemType="Connector"
        isDeleting={isDeleting}
      />

      <SyncHistoryModal
        isOpen={!!historyTarget}
        onClose={() => setHistoryTarget(null)}
        connectorId={historyTarget?._id ?? ""}
        spreadsheetName={historyTarget?.spreadsheet_name ?? ""}
      />
    </>
  );
}
